import type { OrderStatus, RunStatus } from "@the-drop/types";
import { fetchWithClerkJwt } from "./fetch-with-clerk";

/**
 * ADR-115 Phase 2 unified order lifecycle (mirrors API `DispensaryOrderStatusV2`).
 * Surfaced on the dashboard so the "Mark ready for pickup" action can target
 * orders in `prepping` (post-confirm, pre-dispatch-offer).
 */
export type ApiDispensaryOrderStatusV2 =
  | "pending"
  | "prepping"
  | "ready"
  | "assigned"
  | "in_transit"
  | "delivered"
  | "failed"
  | "cancelled";

/** Mirrors **`GET /api/v1/dispensaries/:id/orders`** list rows. */
export type ApiDispensaryOrderListItem = {
  id: string;
  status: OrderStatus;
  /** Phase-2 lifecycle column; `null` only on legacy back-compat rows. */
  statusV2: ApiDispensaryOrderStatusV2 | null;
  totalCents: number;
  /** ADR-125 — `prepaid` orders have nothing due at the door. */
  deliveryPaymentMethod?: "ach" | "cash_at_delivery" | "prepaid";
  createdAt: string;
  updatedAt: string;
  customerFullName: string;
  deliveryCity: string;
  deliveryZip: string;
};

/** Mirrors **`GET /api/v1/dispensaries/:id/runs/active`** rows. */
export type ApiDispensaryActiveRunItem = {
  id: string;
  status: RunStatus;
  transporterId: string;
  driverId: string;
  driverFullName: string;
  driverPhone: string | null;
  updatedAt: string;
  metrcManifestNumber: string | null;
  totalLoadValueCents: number;
  vehiclePlate: string;
  etaMinutes: number | null;
  vehicleYear: number;
  vehicleMake: string;
  vehicleModel: string;
};

export type DispensaryDashboardMetrics = {
  ordersToday: number;
  ordersOnTheWay: number;
  avgDeliveryMinutes: number;
  pendingOrders: number;
  activeTransporterCount: number;
  ordersTodayDelta: number;
  onTheWayDelta: number;
  pendingDelta: number;
};

export type DashboardActiveRun = {
  runId: string;
  etaMinutes: number | null;
  totalLoadValueCents: number;
  driver: { name: string; avatarUrl: string };
  vehicle: { plate: string; year: number; make: string; model: string };
};

export type DashboardRecentOrder = {
  orderId: string;
  etaMinutes: number | null;
  totalCents: number;
  /** ADR-125 — true when an ecommerce partner already charged the customer. */
  prepaidViaPartner: boolean;
  status: "on_the_way" | "pending" | "delivered" | "confirmed" | "failed";
  /** Source status from API (for filtering and actions). */
  apiStatus: OrderStatus;
  /** ADR-115 phase-2 column. Used by the "Mark ready" gate (only `prepping` shows the button). */
  statusV2: ApiDispensaryOrderStatusV2 | null;
  customerFullName: string;
  deliveryCity: string;
  deliveryZip: string;
  driver: { name: string; avatarUrl: string };
};

export type DashboardActiveTransporter = {
  driverId: string;
  name: string;
  avatarUrl: string;
  phone: string;
  etaMinutes: number | null;
  vehicle: { plate: string; year: number; make: string; model: string };
  // Positions live in the live-positions hook (`useDispensaryDriversLive`),
  // not on this list. Pre-this-change `lastLat` / `lastLng` were synthesized
  // client-side as `39.73 + index*0.018` etc. — fake Denver coordinates that
  // pinned every driver in the same spot. Removed entirely so callers don't
  // accidentally re-read fake data; the map sources positions by `driverId`
  // from the socket, same as transporter-web.
};

export type ActiveRunFilter = "all" | "pending" | "on_the_way";

function dispensaryPath(dispensaryId: string, subpath: string): string {
  return `/api/v1/dispensaries/${encodeURIComponent(dispensaryId)}${subpath}`;
}

async function safeJson<T>(res: Response): Promise<T | null> {
  if (res.status === 404) return null;
  if (!res.ok) return null;
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function fetchDispensaryOrdersList(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  opts?: { limit?: number; offset?: number; since?: Date },
): Promise<ApiDispensaryOrderListItem[]> {
  const limit = opts?.limit ?? 120;
  const offset = opts?.offset ?? 0;
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (opts?.since) q.set("since", opts.since.toISOString());
  const res = await fetchWithClerkJwt(getToken, `${dispensaryPath(dispensaryId, "/orders")}?${q}`);
  const data = await safeJson<{ orders: ApiDispensaryOrderListItem[] }>(res);
  return data?.orders ?? [];
}

export async function fetchDispensaryActiveRunsApi(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<ApiDispensaryActiveRunItem[]> {
  const res = await fetchWithClerkJwt(getToken, dispensaryPath(dispensaryId, "/runs/active"));
  const data = await safeJson<{ runs: ApiDispensaryActiveRunItem[] }>(res);
  return data?.runs ?? [];
}

// ── Org-level aggregate ("All locations") ────────────────────────────────────
// Same response shapes as the per-store endpoints, aggregated across the org's
// stores. The org orders endpoint returns the same order fields (incl.
// `statusV2`) plus per-row location info we don't need here.

function orgPath(orgId: string, subpath: string): string {
  return `/api/v1/orgs/${encodeURIComponent(orgId)}${subpath}`;
}

export async function fetchOrgOrdersList(
  getToken: () => Promise<string | null>,
  orgId: string,
  opts?: { limit?: number; since?: Date },
): Promise<ApiDispensaryOrderListItem[]> {
  const q = new URLSearchParams({ limit: String(opts?.limit ?? 120) });
  if (opts?.since) q.set("since", opts.since.toISOString());
  const res = await fetchWithClerkJwt(getToken, `${orgPath(orgId, "/orders")}?${q}`);
  const data = await safeJson<{ orders: ApiDispensaryOrderListItem[] }>(res);
  return data?.orders ?? [];
}

export async function fetchOrgActiveRunsApi(
  getToken: () => Promise<string | null>,
  orgId: string,
): Promise<ApiDispensaryActiveRunItem[]> {
  const res = await fetchWithClerkJwt(getToken, orgPath(orgId, "/runs/active"));
  const data = await safeJson<{ runs: ApiDispensaryActiveRunItem[] }>(res);
  return data?.runs ?? [];
}

export async function fetchOrgDashboardMetricsBundle(
  getToken: () => Promise<string | null>,
  orgId: string,
): Promise<DispensaryDashboardMetrics> {
  const [orders, runs] = await Promise.all([
    fetchOrgOrdersList(getToken, orgId, {
      limit: DASHBOARD_METRICS_ORDERS_LIMIT,
      since: dashboardMetricsSince(),
    }),
    fetchOrgActiveRunsApi(getToken, orgId),
  ]);
  return computeDashboardMetrics(orders, runs);
}

const RUN_PENDING_STATUSES: RunStatus[] = ["proposed", "driver_assigned", "driver_confirmed"];
const RUN_ON_THE_WAY_STATUSES: RunStatus[] = ["en_route_to_dispensary", "at_dispensary", "out_for_delivery"];

export function runMatchesActiveTab(status: RunStatus, tab: ActiveRunFilter): boolean {
  if (tab === "all") return true;
  if (tab === "pending") return RUN_PENDING_STATUSES.includes(status);
  if (tab === "on_the_way") return RUN_ON_THE_WAY_STATUSES.includes(status);
  return false;
}

export function mapOrderStatusToDashboard(status: OrderStatus): DashboardRecentOrder["status"] {
  switch (status) {
    case "pending":
      return "pending";
    case "confirmed":
      return "confirmed";
    case "dispatched":
    case "in_progress":
      return "on_the_way";
    case "completed":
      return "delivered";
    case "failed":
      return "failed";
    case "cancelled":
      return "pending";
    default:
      return "pending";
  }
}

export function mapApiRunToDashboardRun(r: ApiDispensaryActiveRunItem): DashboardActiveRun {
  return {
    runId: r.id,
    etaMinutes: r.etaMinutes,
    totalLoadValueCents: r.totalLoadValueCents,
    driver: { name: r.driverFullName, avatarUrl: "" },
    vehicle: {
      plate: r.vehiclePlate,
      year: r.vehicleYear,
      make: r.vehicleMake,
      model: r.vehicleModel,
    },
  };
}

export function mapOrdersToRecentDashboardOrders(
  orders: ApiDispensaryOrderListItem[],
  limit: number,
): DashboardRecentOrder[] {
  return orders.slice(0, limit).map((o) => ({
    orderId: o.id,
    etaMinutes: null,
    totalCents: o.totalCents,
    prepaidViaPartner: o.deliveryPaymentMethod === "prepaid",
    status: mapOrderStatusToDashboard(o.status),
    apiStatus: o.status,
    statusV2: o.statusV2 ?? null,
    customerFullName: o.customerFullName,
    deliveryCity: o.deliveryCity,
    deliveryZip: o.deliveryZip,
    driver: { name: "—", avatarUrl: "" },
  }));
}

export type ConfirmDispensaryOrderResult =
  | { ok: true; orderId: string; status: string; confirmedAt: string }
  | { ok: false; error: string; httpStatus: number };

/**
 * Mirrors the **`POST /api/v1/dispensaries/:id/orders/:id/mark-ready`** response
 * (`register-dispensary-delivery-ops-routes.ts`). On the happy path we either
 * created a fresh `dispatch_offers` row (`offer != null`) or the producer
 * degraded gracefully (`offer == null` with a `reason`). Idempotent re-call
 * returns `alreadyReady: true` and does not re-emit.
 */
export type MarkDispensaryOrderReadyOk = {
  ok: true;
  orderId: string;
  statusV2: "ready";
  readyForPickupAt: string;
  alreadyReady: boolean;
  /** Producer result. `null` when degraded (Redis missing) or already-ready. */
  offer:
    | {
        offerId: string;
        driverId: string;
        transporterId: string;
        vehicleId: string;
        expiresAt: string;
      }
    | null;
  /** Diagnostic when `offer == null`. Absent on the happy path (`offer != null`). */
  reason?:
    | "already_ready"
    | "redis_unavailable"
    | "no_round_robin_state"
    | "no_transporter_available"
    | "no_eligible_driver"
    | "invalid_order_status"
    | "order_not_found";
};

export type MarkDispensaryOrderReadyResult =
  | MarkDispensaryOrderReadyOk
  | { ok: false; error: string; httpStatus: number };

/**
 * **POST /api/v1/dispensaries/:dispensaryId/orders/:orderId/mark-ready** —
 * post-confirm transition to `ready` that fires `emitDispatchOfferForOrder`
 * (per-order driver offer with 3-minute accept window). The route returns 200
 * on idempotent re-calls (`alreadyReady=true`) and degrades gracefully when
 * the producer can't fire (Redis unavailable, no eligible driver, etc.) — the
 * dashboard surfaces the `reason` so the dispensary knows what to do next.
 */
export async function markDispensaryOrderReady(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  orderId: string,
): Promise<MarkDispensaryOrderReadyResult> {
  const res = await fetchWithClerkJwt(
    getToken,
    dispensaryPath(dispensaryId, `/orders/${encodeURIComponent(orderId)}/mark-ready`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
  if (res.ok) {
    const data = (await res.json()) as MarkDispensaryOrderReadyOk;
    return data;
  }
  let error = "mark_ready_failed";
  try {
    const j = (await res.json()) as {
      error?: string;
      currentStatusV2?: string;
      message?: string;
    };
    if (j.error === "invalid_order_status" && j.currentStatusV2) {
      error = `invalid_status_v2:${j.currentStatusV2}`;
    } else if (j.error && j.message) {
      // Server's defensive try/catch in the mark-ready route attaches the
      // underlying error.message alongside `error: 'mark_ready_failed'`.
      // Surface both so the dispensary worker sees the real cause (e.g.
      // engine SQL failure, Redis hiccup, schema mismatch) instead of a
      // generic code, and so we don't have to log-spelunk every staging
      // failure.
      error = `${j.error}: ${j.message}`;
    } else if (j.error) {
      error = j.error;
    }
  } catch {
    /* ignore */
  }
  return { ok: false, error, httpStatus: res.status };
}

/** POST /api/v1/dispensaries/:dispensaryId/orders/:orderId/confirm — moves **`pending`** → confirmed (dispensary intake). */
export async function confirmDispensaryOrder(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  orderId: string,
  body?: { confirmationNotes?: string | null },
): Promise<ConfirmDispensaryOrderResult> {
  const res = await fetchWithClerkJwt(getToken, dispensaryPath(dispensaryId, `/orders/${encodeURIComponent(orderId)}/confirm`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (res.ok) {
    const data = (await res.json()) as { orderId: string; status: string; confirmedAt: string };
    return { ok: true, orderId: data.orderId, status: data.status, confirmedAt: data.confirmedAt };
  }
  let error = "confirm_failed";
  try {
    const j = (await res.json()) as { error?: string; status?: string };
    if (j.error === "invalid_order_status" && j.status) error = `invalid_status:${j.status}`;
    else if (j.error) error = j.error;
  } catch {
    /* ignore */
  }
  return { ok: false, error, httpStatus: res.status };
}

/** Unique drivers from active runs (for map + list). */
export function mapActiveRunsToTransporters(runs: ApiDispensaryActiveRunItem[]): DashboardActiveTransporter[] {
  const byDriver = new Map<string, ApiDispensaryActiveRunItem>();
  for (const r of runs) {
    if (!byDriver.has(r.driverId)) byDriver.set(r.driverId, r);
  }
  return [...byDriver.values()].map((r) => ({
    driverId: r.driverId,
    name: r.driverFullName,
    avatarUrl: "",
    phone: r.driverPhone?.trim() || "—",
    etaMinutes: r.etaMinutes,
    vehicle: {
      plate: r.vehiclePlate,
      year: r.vehicleYear,
      make: r.vehicleMake,
      model: r.vehicleModel,
    },
  }));
}

export function computeDashboardMetrics(
  orders: ApiDispensaryOrderListItem[],
  runs: ApiDispensaryActiveRunItem[],
): DispensaryDashboardMetrics {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const ordersToday = orders.filter((o) => new Date(o.createdAt) >= startOfToday).length;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const runsOnTheWay = runs.filter((r) => RUN_ON_THE_WAY_STATUSES.includes(r.status)).length;
  const ordersInMotion = orders.filter((o) => o.status === "dispatched" || o.status === "in_progress").length;
  const ordersOnTheWay = runsOnTheWay > 0 ? runsOnTheWay : ordersInMotion;

  const etas = runs.map((r) => r.etaMinutes).filter((m): m is number => m != null && m > 0);
  const avgDeliveryMinutes =
    etas.length > 0 ? Math.round(etas.reduce((a, b) => a + b, 0) / etas.length) : 0;

  const activeTransporterCount = new Set(runs.map((r) => r.driverId)).size;

  return {
    ordersToday,
    ordersOnTheWay,
    avgDeliveryMinutes,
    pendingOrders,
    activeTransporterCount,
    ordersTodayDelta: 0,
    onTheWayDelta: 0,
    pendingDelta: 0,
  };
}

/**
 * Loads orders + active runs and derives dashboard metrics (no separate metrics endpoint).
 */
/**
 * The dashboard derives its metrics from a trailing window of orders rather
 * than a page of them, so these two constants decide what the numbers mean.
 * `WINDOW_DAYS` is the real bound; the limit is headroom above it (must stay
 * within the API's `ORDERS_LIST_MAX_LIMIT`). A window that outgrows the limit
 * would silently under-report rather than error — the reason the old cap of
 * 200 rows was a bug waiting to happen once orders started flowing.
 */
const DASHBOARD_METRICS_WINDOW_DAYS = 30;
const DASHBOARD_METRICS_ORDERS_LIMIT = 5000;

function dashboardMetricsSince(): Date {
  return new Date(Date.now() - DASHBOARD_METRICS_WINDOW_DAYS * 24 * 60 * 60 * 1000);
}

export async function fetchDispensaryDashboardMetricsBundle(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
): Promise<DispensaryDashboardMetrics> {
  const [orders, runs] = await Promise.all([
    fetchDispensaryOrdersList(getToken, dispensaryId, {
      limit: DASHBOARD_METRICS_ORDERS_LIMIT,
      since: dashboardMetricsSince(),
    }),
    fetchDispensaryActiveRunsApi(getToken, dispensaryId),
  ]);
  return computeDashboardMetrics(orders, runs);
}
